/**
 * Idish (probirka, stakan, kolba, ...) holati va unga ta'sirlar.
 *
 * MODEL
 *   Eritma: suv hajmi V (mL) + ionlar (mol) + molekulyar erigan moddalar (kuchsiz elektrolit,
 *   indikator, galogenlar, organik). Organik qatlam (CCl₄): hajm + undagi erigan moddalar.
 *   Qattiq fazalar: cho'kma, kukun, metall bo'lak (mol). Gaz: idish ichida to'plangan (mol)
 *   va joriy ajralish tezligi (mol/s). Harorat T (K). Qizdirish: gorelka quvvati.
 *
 * ASSUMPTIONLAR
 *   1. Eritma zichligi 1000 kg/m³, issiqlik sig'imi 4180 J/(kg·K); qattiq 800; CCl₄ 1590 kg/m³, 850.
 *   2. Kuchli elektrolitlar to'liq dissotsiatsiyalanadi; eruvchan qattiq modda suv bo'lsa darhol eriydi
 *      (molekulyar moddalar — eruvchanlik chegarasigacha).
 *   3. Hajmlar additiv (aralashtirishda hajm saqlanadi), erigan moddalar hajmga ta'sir qilmaydi.
 *   4. Quyishda cho'kma suspenziya sifatida proporsional o'tadi, metall bo'laklar qoladi.
 *   5. H₂SO₄ suyultirish issiqligi: h(c) = 70 kJ/mol · min(1, c/18) (integral qiymatga yaqin).
 */
import { ion } from './ions';
import { species, type Species } from './species';

export type VesselType =
  | 'testTube'
  | 'beaker'
  | 'flask'
  | 'dish'
  | 'crucible'
  | 'conical'
  | 'roundFlask'
  | 'gasJar'
  | 'bigTube';

export const VESSEL_TYPES: VesselType[] = ['testTube', 'bigTube', 'beaker', 'conical', 'flask', 'roundFlask', 'gasJar', 'dish', 'crucible'];

export interface Vessel {
  id: string;
  type: VesselType;
  /** mL */
  capacityMl: number;
  /** Suyuqlik (suvli) hajmi, mL */
  volumeMl: number;
  /** Erigan ionlar, mol */
  ions: Record<string, number>;
  /** Molekulyar erigan moddalar (kuchsiz elektrolitlar, indikatorlar), mol */
  dissolved: Record<string, number>;
  /** Qattiq fazalar, mol */
  solids: Record<string, number>;
  /** Ajralib chiqqan va idishda to'plangan gaz, mol */
  gas: Record<string, number>;
  /** Joriy gaz ajralish tezligi, mol/s */
  gasRate: Record<string, number>;
  /** K */
  T: number;
  heating: boolean;
  /** Gaz naycha orqali boshqa idishga yo'naltirilgan */
  gasTo: string | null;
  /** Ajralgan gaz jami (sinovlar uchun), mol */
  gasTotal: Record<string, number>;
  /** Organik (suv bilan aralashmaydigan) qatlam hajmi, mL */
  organicMl: number;
  /** Organik qatlamdagi erigan moddalar, mol */
  organic: Record<string, number>;
}

export const AMBIENT_T = 293.15;
export const BURNER_POWER = 300; // W (laboratoriya spirt lampasi/gorelka, probirka uchun)
export const DRY_MAX_T = 773; // K — gorelka alangasida quruq modda chegarasi
export const WATER_CP = 4180;
export const SOLID_CP = 800;
export const ORGANIC_CP = 850;
export const ORGANIC_DENSITY = 1590; // kg/m³ (CCl₄)
/** Gaz molyar hajmi (laboratoriya sharoiti), mL/mol */
export const GAS_ML_PER_MOL = 24000;

export function createVessel(id: string, type: VesselType, capacityMl: number): Vessel {
  return {
    id,
    type,
    capacityMl,
    volumeMl: 0,
    ions: {},
    dissolved: {},
    solids: {},
    gas: {},
    gasRate: {},
    T: AMBIENT_T,
    heating: false,
    gasTo: null,
    gasTotal: {},
    organicMl: 0,
    organic: {},
  };
}

export function cloneVessel(v: Vessel): Vessel {
  return {
    ...v,
    ions: { ...v.ions },
    dissolved: { ...v.dissolved },
    solids: { ...v.solids },
    gas: { ...v.gas },
    gasRate: { ...v.gasRate },
    gasTotal: { ...v.gasTotal },
    organicMl: v.organicMl ?? 0,
    organic: { ...(v.organic ?? {}) },
  };
}

export function add(rec: Record<string, number>, key: string, n: number): void {
  const v = (rec[key] ?? 0) + n;
  if (v <= 1e-15) delete rec[key];
  else rec[key] = v;
}

/** Suyuqlik umumiy hajmi (suv + organik), mL */
export function liquidVolume(v: Vessel): number {
  return v.volumeMl + (v.organicMl ?? 0);
}

/** Eritma massasi, kg */
export function liquidMass(v: Vessel): number {
  return v.volumeMl / 1e6 * 1000;
}

function organicMass(v: Vessel): number {
  return ((v.organicMl ?? 0) / 1e6) * ORGANIC_DENSITY;
}

/** Qattiq fazalar massasi, kg */
export function solidMass(v: Vessel): number {
  let m = 0;
  for (const [id, n] of Object.entries(v.solids)) m += n * species(id).molarMass;
  return m;
}

/** Idish tarkibi massasi (eritma + organik qatlam + qattiq), kg */
export function contentMass(v: Vessel): number {
  let m = liquidMass(v) + solidMass(v) + organicMass(v);
  for (const [id, n] of Object.entries(v.ions)) m += n * ion(id).molarMass;
  for (const [id, n] of Object.entries(v.dissolved)) m += n * species(id).molarMass;
  for (const [id, n] of Object.entries(v.organic ?? {})) m += n * species(id).molarMass;
  return m;
}

/** Issiqlik sig'imi, J/K */
export function heatCapacity(v: Vessel): number {
  return Math.max(5, liquidMass(v) * WATER_CP + solidMass(v) * SOLID_CP + organicMass(v) * ORGANIC_CP);
}

/** Konsentratsiya, mol/L */
export function conc(v: Vessel, ionId: string): number {
  if (v.volumeMl <= 1e-9) return 0;
  return ((v.ions[ionId] ?? 0) / v.volumeMl) * 1000;
}

/** H₂SO₄ "ortiqcha" entalpiyasi (suyultirilganda ajraladi), J */
function sulfuricEnthalpy(ions: Record<string, number>, volumeMl: number): number {
  if (volumeMl <= 0) return 0;
  const nAcid = Math.min(ions.SO4 ?? 0, (ions.H ?? 0) / 2);
  if (nAcid <= 0) return 0;
  const c = (nAcid / volumeMl) * 1000;
  return nAcid * 70000 * Math.min(1, c / 18);
}

/**
 * Modda n molini eritmaga kiritish: kuchli elektrolit → ionlar, kuchsiz/indikator → molekula,
 * aralashma → komponentlar. Qaytaradi: eritilmay qolgan miqdor (eruvchanlik chegarasi), mol.
 */
export function dissolveSpecies(v: Vessel, s: Species, n: number): number {
  if (n <= 0) return 0;
  if (s.components) {
    for (const [cid, k] of Object.entries(s.components)) dissolveSpecies(v, species(cid), n * k);
    return 0;
  }
  if (s.kind === 'water') {
    v.volumeMl += (n * s.molarMass) / 1000 * 1e6;
    return 0;
  }
  if (s.ions && s.Ka === undefined && s.Kb === undefined) {
    for (const [i, k] of Object.entries(s.ions)) add(v.ions, i, n * k);
    return 0;
  }
  add(v.dissolved, s.id, n);
  return 0;
}

/** Molekulyar modda (I₂, Br₂) eruvchanlik chegarasi bilan; I⁻ bo'lsa I₂ yaxshi eriydi (I₃⁻) */
export function solubleLimit(v: Vessel, s: Species): number {
  if (s.solubility === undefined) return Infinity;
  // Gazlar issiq suvda kam eriydi (Genri konstantasi): ~2,5 %/K
  const tf = s.kind === 'gas' ? Math.exp(-0.025 * (v.T - 293.15)) : 1;
  let lim = (s.solubility * tf * v.volumeMl) / 1000;
  if (s.id === 'I2') lim += v.ions.I ?? 0;
  return Math.max(0, lim - (v.dissolved[s.id] ?? 0));
}

/** Qattiq modda (g) qo'shish */
export function addSolid(v: Vessel, speciesId: string, grams: number): void {
  const s = species(speciesId);
  const n = grams / 1000 / s.molarMass;
  if (n <= 0) return;
  if (s.soluble && v.volumeMl > 0 && s.kind !== 'metal') {
    const nd = Math.min(n, solubleLimit(v, s));
    if (nd > 0) dissolveSpecies(v, s, nd);
    if (n - nd > 1e-15) add(v.solids, speciesId, n - nd);
    if (s.dHsol) v.T -= (nd * s.dHsol) / heatCapacity(v);
  } else {
    add(v.solids, speciesId, n);
  }
}

/** Eritma (mL, mol/L) qo'shish; reagent harorati atrof */
export function addSolution(v: Vessel, speciesId: string, ml: number, molarity: number): void {
  const s = species(speciesId);
  if (ml <= 0) return;
  if (s.immiscible) {
    // Organik suyuqlik (CCl₄): alohida qatlam
    const C0 = heatCapacity(v);
    v.organicMl = (v.organicMl ?? 0) + ml;
    const Cadd = (ml / 1e6) * ORGANIC_DENSITY * ORGANIC_CP;
    v.T = (v.T * C0 + AMBIENT_T * Cadd) / (C0 + Cadd);
    return;
  }
  const Cbefore = heatCapacity(v);
  const Ebefore = sulfuricEnthalpy(v.ions, v.volumeMl);
  const n = (molarity * ml) / 1000;
  // Qo'shilayotgan reagentning o'z entalpiyasi (alohida idishda)
  const tmp = createVessel('_', 'beaker', ml);
  tmp.volumeMl = ml;
  if (s.kind !== 'water') dissolveSpecies(tmp, s, n);
  const Eadded = sulfuricEnthalpy(tmp.ions, ml);
  v.volumeMl += ml;
  if (s.kind !== 'water') dissolveSpecies(v, s, n);
  // Harorat aralashuvi
  const Cadd = (ml / 1000) * WATER_CP;
  v.T = (v.T * Cbefore + AMBIENT_T * Cadd) / (Cbefore + Cadd);
  const released = Ebefore + Eadded - sulfuricEnthalpy(v.ions, v.volumeMl);
  if (released > 0) v.T += released / heatCapacity(v);
}

function moveFraction(from: Record<string, number>, to: Record<string, number>, g: number): void {
  for (const [i, n] of Object.entries(from)) {
    add(to, i, n * g);
    add(from, i, -n * g);
  }
}

/** Suyuqlikning `fraction` qismini boshqa idishga quyish (ionlar, erigan moddalar, organik qatlam, cho'kma suspenziyasi) */
export function pour(from: Vessel, to: Vessel, fraction: number): void {
  const f = Math.min(1, Math.max(0, fraction));
  const total = liquidVolume(from);
  if (f <= 0 || total <= 0) return;
  const room = Math.max(0, to.capacityMl - liquidVolume(to));
  const ml = Math.min(total * f, room);
  const g = ml / total;
  if (g <= 0) return;
  const Cto = heatCapacity(to);
  const Eto = sulfuricEnthalpy(to.ions, to.volumeMl);
  const aqMl = from.volumeMl * g;
  const orgMl = (from.organicMl ?? 0) * g;
  const Cmoved = (aqMl / 1000) * WATER_CP + (orgMl / 1e6) * ORGANIC_DENSITY * ORGANIC_CP;
  const Emoved = sulfuricEnthalpy(from.ions, from.volumeMl) * g;
  moveFraction(from.ions, to.ions, g);
  moveFraction(from.dissolved, to.dissolved, g);
  from.organic ??= {};
  to.organic ??= {};
  moveFraction(from.organic, to.organic, g);
  for (const [i, n] of Object.entries(from.solids)) {
    if (species(i).kind === 'metal' || species(i).coating) continue; // bo'laklar, qoplama qoladi
    add(to.solids, i, n * g);
    add(from.solids, i, -n * g);
  }
  from.volumeMl -= aqMl;
  to.volumeMl += aqMl;
  from.organicMl = (from.organicMl ?? 0) - orgMl;
  to.organicMl = (to.organicMl ?? 0) + orgMl;
  to.T = (to.T * Cto + from.T * Cmoved) / (Cto + Cmoved);
  const released = Eto + Emoved - sulfuricEnthalpy(to.ions, to.volumeMl);
  if (released > 0) to.T += released / heatCapacity(to);
}

/** Filtrlash: suyuqlik (filtrat) boshqa idishga o'tadi, qattiq fazalar filtrda (manba idishda) qoladi */
export function filter(from: Vessel, to: Vessel): void {
  const total = liquidVolume(from);
  if (total <= 0) return;
  const room = Math.max(0, to.capacityMl - liquidVolume(to));
  const g = Math.min(1, room / total);
  if (g <= 0) return;
  const solids = from.solids;
  from.solids = {};
  pour(from, to, g);
  from.solids = solids;
}

export function emptyVessel(v: Vessel): void {
  v.volumeMl = 0;
  v.ions = {};
  v.dissolved = {};
  v.solids = {};
  v.gas = {};
  v.gasRate = {};
  v.T = AMBIENT_T;
  v.heating = false;
  v.organicMl = 0;
  v.organic = {};
}
